import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react';

const outerHeight = (node: HTMLElement) => node.offsetHeight;
const visibleHeight = (node: HTMLElement) => node.clientHeight;

function useObservedHeight(node: HTMLElement | null, measure: (node: HTMLElement) => number): number {
  const [height, setHeight] = useState(0);
  useEffect(() => {
    if (!node || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => setHeight(Math.round(measure(node))));
    observer.observe(node);
    return () => observer.disconnect();
  }, [node, measure]);
  return height;
}

export function stickyShellVars(barHeight: number, scrollAreaHeight: number): CSSProperties {
  return {
    '--emr-bar-h': `${barHeight}px`,
    ...(scrollAreaHeight > 0 ? { '--emr-scroll-h': `${scrollAreaHeight}px` } : {}),
  } as CSSProperties;
}

export interface StickyShell {
  attachPage: (node: HTMLDivElement | null) => void;
  attachBar: (node: HTMLDivElement | null) => void;
  style: CSSProperties;
  scrollToTop: () => void;
}

export function useStickyShell(): StickyShell {
  const [barNode, setBarNode] = useState<HTMLDivElement | null>(null);
  const [scrollArea, setScrollArea] = useState<HTMLElement | null>(null);
  const barHeight = useObservedHeight(barNode, outerHeight);
  const scrollAreaHeight = useObservedHeight(scrollArea, visibleHeight);
  const attachPage = useCallback((node: HTMLDivElement | null) => setScrollArea(node?.closest<HTMLElement>('.app-scroll') ?? null), []);
  const style = useMemo(() => stickyShellVars(barHeight, scrollAreaHeight), [barHeight, scrollAreaHeight]);
  const scrollToTop = useCallback(() => scrollArea?.scrollTo({ top: 0 }), [scrollArea]);
  return { attachPage, attachBar: setBarNode, style, scrollToTop };
}
